<template>
  <v-card
    v-if="shouldShowPanel"
    class="pa-6"
    v-motion
    :initial="{ y: 30 }"
    :enter="{ y: 0 }"
  >
    <v-row>
      <v-col cols="12">
        <div class="text-body-1 font-weight-bold">Nomogram</div>
        <div class="text-caption text-disabled">
          See how you compare to peers ({{ bankLabel }})
        </div>
      </v-col>

      <v-col cols="12" v-if="visibleMetricOptions.length > 1">
        <v-btn-toggle v-model="metric" mandatory divided density="comfortable">
          <v-btn
            v-for="opt in visibleMetricOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <div v-if="nomogramStore.loading" class="text-caption text-disabled mt-4">
      Loading…
    </div>

    <div v-else-if="nomogramStore.error" class="text-caption text-error mt-4">
      Failed to load nomogram data.
    </div>

    <div v-else class="mt-4">
      <div v-if="!currentHistogram" class="text-caption text-disabled">
        No histogram data yet.
      </div>

      <template v-else>
        <NormogramChart
          :bins="currentHistogram.bins || []"
          :sample-size="currentHistogram.sampleSize || 0"
          :mean="currentHistogram.mean ?? null"
          :p10="currentHistogram.p10 ?? null"
          :p25="currentHistogram.p25 ?? null"
          :p50="currentHistogram.p50 ?? null"
          :p75="currentHistogram.p75 ?? null"
          :p90="currentHistogram.p90 ?? null"
          :user-score="userScore"
          :pass-mark="currentHistogram.passMark ?? null"
          :pass-label="currentHistogram.passLabel || ''"
        />

        <div v-if="metric === 'first'" class="text-caption text-disabled mt-2">
          First-attempt data is collected forward-only.
        </div>
      </template>
    </div>
  </v-card>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useCurrentUser } from "vuefire";
import { BANK_LABEL_BY_ID } from "@/constants/banks";
import { useNomogramStore } from "@/store/nomogram";
import NormogramChart from "@/components/NormogramChart.vue";

const props = defineProps({
  bankId: { type: String, required: true },
});

const nomogramStore = useNomogramStore();
const currentUser = useCurrentUser();
const metric = ref("average");

const bankLabel = computed(
  () => BANK_LABEL_BY_ID[props.bankId] || props.bankId,
);

const histogramKey = computed(() => `${props.bankId}:${metric.value}`);
const currentHistogram = computed(
  () => nomogramStore.histograms[histogramKey.value],
);
const currentUserMetrics = computed(
  () => nomogramStore.userMetrics[props.bankId],
);

const userScore = computed(() => {
  const m = currentUserMetrics.value || {};
  if (metric.value === "average") return m.averageScore ?? null;
  if (metric.value === "last") return m.lastScore ?? null;
  if (metric.value === "first") return m.firstAttemptScore ?? null;
  return null;
});

const averageHistogram = computed(
  () => nomogramStore.histograms[`${props.bankId}:average`],
);
const lastHistogram = computed(
  () => nomogramStore.histograms[`${props.bankId}:last`],
);
const firstHistogram = computed(
  () => nomogramStore.histograms[`${props.bankId}:first`],
);

const MIN_SAMPLE_SIZE = 20;
const MCQ_BANKS = new Set([
  "part-2-question-bank",
  "part-1-question-bank",
  "duke-elder-question-bank",
]);

const metricOptions = computed(() => [
  {
    value: "average",
    label: "Average attempt",
    histogram: averageHistogram.value,
  },
  { value: "last", label: "Last attempt", histogram: lastHistogram.value },
  { value: "first", label: "First attempt", histogram: firstHistogram.value },
]);

const visibleMetricOptions = computed(() =>
  metricOptions.value.filter(
    (opt) => Number(opt?.histogram?.sampleSize || 0) >= MIN_SAMPLE_SIZE,
  ),
);

const shouldShowPanel = computed(() => visibleMetricOptions.value.length > 0);

const pickDefaultMetric = () =>
  visibleMetricOptions.value[0]?.value || "average";

const lastLooksSameAsAverage = computed(() => {
  if (metric.value !== "last") return false;
  if (!MCQ_BANKS.has(props.bankId)) return false;
  const avg = averageHistogram.value;
  const last = lastHistogram.value;
  if (!avg || !last) return false;
  const avgBins = JSON.stringify(avg?.bins || []);
  const lastBins = JSON.stringify(last?.bins || []);
  const avgN = Number(avg?.sampleSize || 0);
  const lastN = Number(last?.sampleSize || 0);
  return (
    avgN >= MIN_SAMPLE_SIZE &&
    lastN >= MIN_SAMPLE_SIZE &&
    avgN === lastN &&
    avgBins === lastBins
  );
});

watch(
  () => props.bankId,
  async (bankId) => {
    if (!bankId) return;
    await nomogramStore.fetchBankNomograms(bankId, { force: true });
    metric.value = pickDefaultMetric();
  },
  { immediate: true },
);

watch(
  () => currentUser.value?.uid,
  async (uid) => {
    if (!uid || !props.bankId) return;
    await nomogramStore.fetchUserMetrics(props.bankId, { force: true });
  },
  { immediate: true },
);

watch(
  visibleMetricOptions,
  () => {
    if (visibleMetricOptions.value.length === 0) return;
    const visibleValues = new Set(
      visibleMetricOptions.value.map((o) => o.value),
    );
    if (!visibleValues.has(metric.value)) {
      metric.value = pickDefaultMetric();
    }
  },
  { immediate: true, deep: true },
);
</script>
